// Proje ekleme/düzenleme modal'ı
import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { FiX } from 'react-icons/fi';
import { projectService } from '../../services/projectService';
import { customerService } from '../../services/customerService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const ProjectModal = ({ project, customer, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Kullanıcı tipini kontrol et
  const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
  const isAdminOrManager = currentUser?.user_type === 'Admin' || 
                          currentUser?.user_type === 'Manager' || 
                          currentUser?.user_type === 'admin' || 
                          currentUser?.user_type === 'manager';
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm();

  // Proje durumunu izle
  const projectStatus = watch('project_status');

  // Müşteri ve kullanıcı listelerini al
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Müşteri listesini al
        const customersResponse = await customerService.getCustomers({ limit: 1000 });
        setCustomers(customersResponse.customers || []);
        
        // Kullanıcı listesini al
        const usersResponse = await userService.getUsers({ limit: 1000 });
        setUsers(usersResponse.users || []);
      } catch (error) {
        console.error('Veri yüklenemedi:', error);
        toast.error('Müşteri ve kullanıcı listesi yüklenemedi');
      }
    };

    fetchData();
  }, []);

  // Proje oluşturma/güncelleme mutation'ı
  const saveProjectMutation = useMutation(
    (data) => {
      if (project) {
        return projectService.updateProject(project.id, data);
      } else {
        return projectService.createProject(data);
      }
    },
    {
      onSuccess: (data) => {
        toast.success(project ? 'Proje güncellendi' : 'Proje oluşturuldu');
        queryClient.invalidateQueries('projects');
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'İşlem başarısız');
      }
    }
  );

  // Form'u proje verileriyle doldur
  const fillForm = useCallback(() => {
    if (project) {
      // Form değerlerini manuel olarak set et
      setValue('project_name', project.project_name || '');
      setValue('project_description', project.project_description || '');
      setValue('project_status', project.project_status || 'Aktif');
      setValue('customer_id', project.customer_id || '');
      setValue('assigned_to', project.assigned_to || '');
      setValue('project_opportunity', project.project_opportunity || '');
      setValue('project_products', project.project_products || '');
      setValue('project_visibility', project.project_visibility || 'Public');
      // close_date varsa formatla (YYYY-MM-DD formatına çevir)
      if (project.close_date) {
        const date = new Date(project.close_date);
        const formattedDate = date.toISOString().split('T')[0];
        setValue('close_date', formattedDate);
      }
    } else {
      // Yeni proje oluştururken otomatik değerler
      setValue('project_status', 'Aktif'); // Yeni projeler her zaman Aktif olarak başlar
      setValue('project_visibility', 'Public'); // Varsayılan görünüm Public
      
      if (customer) {
        // Müşteri detay sayfasından çağrıldığında müşteri ID'sini set et
        setValue('customer_id', customer.id);
      }
    }
    
    // User tipindeki kullanıcılar için kendi ID'lerini set et
    if (currentUser?.user_type === 'user') {
      setValue('assigned_to', currentUser.id);
    }
  }, [project, customer, setValue]);

  useEffect(() => {
    // Kısa bir gecikme ile form doldurma işlemini yap
    const timer = setTimeout(() => {
      fillForm();
    }, 100);
    return () => clearTimeout(timer);
  }, [project, customer, fillForm]);

  // Proje durumu "Başarılı Tamamlandı" seçildiğinde tarih alanını otomatik doldur
  useEffect(() => {
    if (projectStatus === 'Başarılı Tamamlandı') {
      // Eğer close_date boşsa, şu anki tarihi set et
      const currentDate = watch('close_date');
      if (!currentDate) {
        const today = new Date().toISOString().split('T')[0];
        setValue('close_date', today);
      }
    }
  }, [projectStatus, setValue, watch]);

  // Form gönderimi
  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      await saveProjectMutation.mutateAsync(data);
    } catch (error) {
      console.error('Form gönderim hatası:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-4xl">
        {/* Modal header */}
        <div className="modal-header">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">
              {project ? 'Proje Düzenle' : 'Yeni Proje'}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* Modal body */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Proje adı ve Proje Görünüm */}
            <div className="md:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Proje adı */}
                <div className="md:col-span-2">
                  <label htmlFor="project_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Proje Adı *
                  </label>
                  <input
                    id="project_name"
                    type="text"
                    {...register('project_name', { required: 'Proje adı gerekli' })}
                    className={`input-field ${errors.project_name ? 'input-error' : ''}`}
                    placeholder="Proje adını girin"
                  />
                  {errors.project_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.project_name.message}</p>
                  )}
                </div>

                {/* Proje Görünüm - Sadece Admin ve Manager için */}
                {isAdminOrManager && (
                  <div>
                    <label htmlFor="project_visibility" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Proje Görünüm
                    </label>
                    <select
                      id="project_visibility"
                      {...register('project_visibility')}
                      className="input-field"
                    >
                      <option value="Public">Herkes</option>
                      <option value="Private">Yalnızca ben</option>
                    </select>
                  </div>
                )}
              </div>
              
            </div>

            {/* Müşteri seçimi */}
            <div className="md:col-span-2">
              <label htmlFor="customer_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Müşteri *
              </label>
              {customer ? (
                <div className="input-field bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                  {customer.customer_name}
                </div>
              ) : (
                <select
                  id="customer_id"
                  {...register('customer_id', { required: 'Müşteri seçimi gerekli' })}
                  className={`input-field ${errors.customer_id ? 'input-error' : ''}`}
                >
                  <option value="">Müşteri seçin</option>
                  {customers
                    .filter((customer) => {
                      // User tipindeki kullanıcılar sadece kendi müşterilerini görebilir
                      if (currentUser?.user_type === 'user') {
                        return customer.customer_account_manager === currentUser.id;
                      }
                      // Admin ve Manager tüm müşterileri görebilir
                      return true;
                    })
                    .map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.customer_name} {customer.account_manager_name ? `(Account Manager: ${customer.account_manager_name})` : ''}
                      </option>
                    ))}
                </select>
              )}
              {errors.customer_id && (
                <p className="mt-1 text-sm text-red-600">{errors.customer_id.message}</p>
              )}
            </div>

            {/* Proje açıklaması */}
            <div>
              <label htmlFor="project_description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Proje Açıklaması
              </label>
              <textarea
                id="project_description"
                rows={2}
                {...register('project_description')}
                className="input-field"
                placeholder="Proje açıklamasını girin"
              />
            </div>

            {/* Ürünler */}
            <div>
              <label htmlFor="project_products" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ürünler
              </label>
              <textarea
                id="project_products"
                rows={2}
                {...register('project_products')}
                className="input-field"
                placeholder="Ürün bilgilerini girin"
              />
            </div>

            {/* Durum, Fırsat, Atanan Kullanıcı - Tek satır */}
            <div className="md:col-span-2">
              <div className={`grid grid-cols-1 gap-4 ${project ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                {/* Proje durumu - Sadece düzenleme sırasında göster */}
                {project && (
                  <div>
                    <label htmlFor="project_status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Durum
                    </label>
                    <select
                      id="project_status"
                      {...register('project_status')}
                      className="input-field"
                    >
                      <option value="Aktif">Aktif</option> 
                      <option value="Beklemede">Beklemede</option> 
                      <option value="Başarılı Tamamlandı">Başarılı Tamamlandı</option>
                      <option value="Başarısız Tamamlandı">Başarısız Tamamlandı</option>
                      <option value="İptal">İptal</option>
                    </select>
                  </div>
                )}

                {/* Fırsat */}
                <div>
                  <label htmlFor="project_opportunity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fırsat *
                  </label>
                  <select
                    id="project_opportunity"
                    {...register('project_opportunity', { required: 'Fırsat seviyesi gerekli' })}
                    className={`input-field ${errors.project_opportunity ? 'input-error' : ''}`}
                  >
                    <option value="">Fırsat seviyesi seçin</option>
                    <option value="0">Henüz belli değil</option>
                    <option value="1">1 - Çok Kötü</option>
                    <option value="2">2 - Kötü</option>
                    <option value="3">3 - Orta</option>
                    <option value="4">4 - İyi</option>
                    <option value="5">5 - Çok İyi</option>
                  </select>
                  {errors.project_opportunity && (
                    <p className="mt-1 text-sm text-red-600">{errors.project_opportunity.message}</p>
                  )}
                </div>

                {/* Atanan Kullanıcı */}
                <div>
                  <label htmlFor="assigned_to" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Atanan Kullanıcı *
                  </label>
                  {currentUser?.user_type === 'user' ? (
                    <div className="input-field bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                      {currentUser.user_fullname}
                    </div>
                  ) : (
                    <select
                      id="assigned_to"
                      {...register('assigned_to', { required: 'Kullanıcı seçimi gerekli' })}
                      className={`input-field ${errors.assigned_to ? 'input-error' : ''}`}
                    >
                      <option value="">Kullanıcı seçin</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.user_fullname}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.assigned_to && (
                    <p className="mt-1 text-sm text-red-600">{errors.assigned_to.message}</p>
                  )}
                </div>
              </div>

            </div>

            {/* Kapanış Tarihi - Sadece "Başarılı Tamamlandı" durumunda göster */}
            {project && projectStatus === 'Başarılı Tamamlandı' && (
              <div className="md:col-span-2">
                <label htmlFor="close_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kapanış Tarihi
                </label>
                <input
                  id="close_date"
                  type="date"
                  {...register('close_date')}
                  className="input-field"
                />
              </div>
            )}

          </div>

          {/* Modal footer */}
          <div className="flex items-center justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              İptal
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Kaydediliyor...' : (project ? 'Güncelle' : 'Oluştur')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectModal;
